import styled from '@emotion/styled';
import { forwardRef, useCallback } from 'react';

import { Checkbox } from '../Checkbox';
import { Message } from '../Message';
import { Radio } from '../Radio';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { theme } from '../../theme';

import type { RiskItemCardProps } from './RiskItemCard.types';
import type { Theme } from '../../theme';

/**
 * Card container with selection, error, and disabled states
 */
const CardContainer = styled.div<{
  theme?: Theme;
  $isSelected: boolean;
  $hasError: boolean;
  $isDisabled: boolean;
  $accentColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background-color: ${({ $isSelected, $isDisabled, $accentColor, theme: t }) =>
    $isDisabled
      ? t.colors.background.default
      : $isSelected
        ? `color-mix(in srgb, ${$accentColor || t.colors.highlight} 8%, white)`
        : t.colors.white};
  border: 1px solid
    ${({ $isSelected, $hasError, $isDisabled, $accentColor, theme: t }) =>
      $hasError
        ? t.colors.status.error
        : $isDisabled
          ? t.colors.border.light
          : $isSelected
            ? $accentColor || t.colors.highlight
            : t.colors.border.light};
  border-radius: 8px;
  cursor: ${({ $isDisabled, $hasError }) =>
    $isDisabled || $hasError ? 'not-allowed' : 'pointer'};
  transition: ${theme.transitions.fast};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};

  &:hover {
    background-color: ${({ $isSelected, $isDisabled, $accentColor, theme: t }) =>
      $isDisabled
        ? t.colors.background.default
        : $isSelected
          ? `color-mix(in srgb, ${$accentColor || t.colors.highlight} 8%, white)`
          : t.colors.background.default};
    border-color: ${({
      $hasError,
      $isSelected,
      $isDisabled,
      $accentColor,
      theme: t,
    }) =>
      $hasError
        ? t.colors.status.error
        : $isDisabled
          ? t.colors.border.light
          : $isSelected
            ? $accentColor || t.colors.highlight
            : t.colors.border.medium};
  }

  &:focus-visible {
    outline: 2px solid ${({ $accentColor, theme: t }) => $accentColor || t.colors.highlight};
    outline-offset: 2px;
  }
`;

/**
 * Icon container with customizable background and foreground colors
 */
const IconContainer = styled.div<{
  $bgColor: string;
  $fgColor: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${({ $bgColor }) => $bgColor};
  color: ${({ $fgColor }) => $fgColor};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;

/**
 * Title text with truncation
 */
const Title = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
`;

/**
 * Subtitle wrapper for secondary information
 * Also overrides font-size on any Chip components inside
 */
const SubtitleWrapper = styled.div`
  font-size: ${theme.typography.fontSize.tn};
  color: ${theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  /* Override Chip's internal font-size to use tn */
  > div > div,
  div[class*="Chip"] {
    font-size: ${theme.typography.fontSize.tn};
  }
`;

/**
 * Wrapper that applies tn font size to Chip components passed as badge
 */
const BadgeWrapper = styled.span`
  display: inline-flex;
  
  /* Override Chip's internal font-size to use tn */
  > div {
    font-size: ${theme.typography.fontSize.tn};
  }
`;

/**
 * Remove button with danger hover state
 */
const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.text.muted};
  border-radius: 4px;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.status.error};
    background-color: color-mix(
      in srgb,
      ${theme.colors.status.error} 10%,
      ${theme.colors.white}
    );
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.highlight};
    outline-offset: 1px;
  }

  svg {
    display: block;
  }
`;

/**
 * Close icon for remove button
 */
const CloseIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4L12 12M12 4L4 12" />
  </svg>
);

/**
 * RiskItemCard - A selectable card for file/page items in Risk Reviews
 *
 * A versatile card component that supports:
 * - Selected, error, and disabled states
 * - Single (radio) or multi (checkbox) selection modes
 * - Custom icon with background/foreground colors
 * - Title and subtitle with text truncation
 * - Optional badge (Chip with tn font size applied automatically)
 * - Remove button
 * - Custom accent color for selection highlighting
 *
 * Used for displaying files in the review queue and pages for URL capture.
 *
 * @example
 * ```tsx
 * // File card with badge
 * <RiskItemCard
 *   icon={{ content: 'PDF', bgColor: '#FFEBEE', fgColor: '#C62828' }}
 *   title="document.pdf"
 *   badge={<Chip size="small" label="1.2 MB" />}
 *   selected={selectedIndex === 0}
 *   onClick={() => setSelectedIndex(0)}
 *   onRemove={() => removeFile(0)}
 * />
 *
 * // Page card with multi selection
 * <RiskItemCard
 *   icon={{ content: <FavIcon src={url} />, bgColor: 'transparent' }}
 *   title="Page Title"
 *   subtitle="https://example.com/page"
 *   selectionMode="multi"
 *   selected={selectedIds.has(id)}
 *   disabled={isMaxSelected && !selectedIds.has(id)}
 *   onClick={() => toggleSelection(id)}
 * />
 * ```
 */
export const RiskItemCard = forwardRef<HTMLDivElement, RiskItemCardProps>(
  (
    {
      selected = false,
      error = false,
      disabled = false,
      icon,
      title,
      subtitle,
      badge,
      errorMessage,
      onRemove,
      onClick,
      selectionMode = 'single',
      showSelectionControl = true,
      name,
      value,
      accentColor,
      className,
      style,
      dataTestId,
      ariaLabel,
    },
    ref
  ) => {
    const hasError = error || !!errorMessage;
    const isMultiMode = selectionMode === 'multi';
    const isInteractionDisabled = hasError || disabled;

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isInteractionDisabled) return;
        onClick?.();
      },
      [isInteractionDisabled, onClick]
    );

    const handleRemove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove?.();
      },
      [onRemove]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isInteractionDisabled) return;
          onClick?.();
        }
      },
      [isInteractionDisabled, onClick]
    );

    const renderSelectionControl = () => {
      if (!showSelectionControl) return null;

      if (isMultiMode) {
        return (
          <Checkbox
            checked={selected && !hasError}
            disabled={isInteractionDisabled}
            color={accentColor}
            aria-label={ariaLabel || `Select ${title}`}
            tabIndex={-1}
            onChange={() => {
              if (!isInteractionDisabled) {
                onClick?.();
              }
            }}
          />
        );
      }

      return (
        <Radio
          name={name}
          value={value ?? title}
          checked={selected && !hasError}
          disabled={hasError}
          color={accentColor}
          aria-label={ariaLabel || `Select ${title}`}
        />
      );
    };

    const renderIcon = () => {
      if (!icon) return null;

      return (
        <IconContainer
          $bgColor={icon.bgColor || theme.colors.grey[100]}
          $fgColor={icon.fgColor || theme.colors.grey[600]}
          aria-hidden="true"
        >
          {icon.content}
        </IconContainer>
      );
    };

    const renderSubtitle = () => {
      if (errorMessage) {
        return (
          <Message variant="error" showIcon={false} size="sm">
            {errorMessage}
          </Message>
        );
      }

      if (!subtitle && !badge) return null;

      return (
        <SubtitleWrapper>
          <Stack direction="horizontal" gap={2} align="center">
            {badge && <BadgeWrapper>{badge}</BadgeWrapper>}
            {subtitle}
          </Stack>
        </SubtitleWrapper>
      );
    };

    return (
      <CardContainer
        ref={ref}
        className={className}
        style={style}
        $isSelected={selected}
        $hasError={hasError}
        $isDisabled={disabled}
        $accentColor={accentColor}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="option"
        aria-selected={selected}
        aria-disabled={isInteractionDisabled}
        data-testid={dataTestId}
      >
        {renderSelectionControl()}

        {renderIcon()}

        <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
          <Tooltip content={title} onlyWhenOverflow position="top">
            <Title>{title}</Title>
          </Tooltip>
          {renderSubtitle()}
        </Stack>

        {onRemove && (
          <Tooltip content="Remove" position="top">
            <RemoveButton
              onClick={handleRemove}
              aria-label={`Remove ${title}`}
              type="button"
            >
              <CloseIcon />
            </RemoveButton>
          </Tooltip>
        )}
      </CardContainer>
    );
  }
);

RiskItemCard.displayName = 'RiskItemCard';

export default RiskItemCard;
